import axios from "axios";
import i18n from "./i18n";

const AUTHENTICATE_URL = `${process.env.REACT_APP_API_URL}/cmms/api/user/authenticate`;

// initializeApp() runs both at module load and again right after a successful login
// (Login.js), so guard registration to avoid piling up a duplicate response interceptor on
// every login within the same tab session - each one would independently redirect on a 401.
let responseInterceptorRegistered = false;
let unhandledRejectionsSaid = false;

/**
 * 1.6's 3.7 (owner, 17 Oct 2026): a request whose failure nothing caught reaches the browser as an unhandled rejection,
 * and the browser prints it whole - an axios error with its request body and its bearer token. Cancelled here, and said
 * as its status only. Each call site still catches its own; this is for one that doesn't.
 */
export function sayAnUnhandledRejection(event) {
  event.preventDefault();
  const error = event.reason;
  console.error("A failure nothing handled:", error?.response?.status);
}

// A failure's words by its code; any other failure says failureUnexpected.
const FAILURE_WORDS = {
  DATA_INTEGRITY_VIOLATION: () => i18n.t("failureNotSaved"),
  AUDIT_NOT_RECORDED: () => i18n.t("failureAuditNotRecorded"),
  NATIONAL_ID_UNAVAILABLE: () => i18n.t("failureNationalIdUnavailable"),
  NATIONAL_ID_NOT_OPENED: () => i18n.t("failureNationalIdNotOpened"),
  SIGN_IN_NOT_CHECKED: () => i18n.t("failureSignInNotChecked"),
  SIGN_IN_NOT_RECORDED: () => i18n.t("failureSignInNotRecorded"),
};

// The refusals the server codes for the reader's words (the owner's decisions of 28 Oct 2026, night, 1 and 2).
const REFUSAL_WORDS = {
  USERNAME_TAKEN: () => i18n.t("refusalUsernameTaken"),
  // Answer 8 (cmms existence-leaks-plan.md, session 4): where a username is set - registration, an account opened for a
  // member - the screen says why it refuses one holding a personnummer's or an address's shape.
  USERNAME_LIKE_A_PERSONAL_NUMBER: () => i18n.t("refusalUsernameLikePersonalNumber"),
  USERNAME_LIKE_AN_EMAIL_ADDRESS: () => i18n.t("refusalUsernameLikeEmailAddress"),
  CHURCH_CODE_TAKEN: () => i18n.t("refusalChurchCodeTaken"),
  CHURCH_HAS_MEMBERS: () => i18n.t("refusalChurchHasMembers"),
  CHURCH_SERVICE_HAS_ACTIVITIES: () => i18n.t("refusalChurchServiceHasActivities"),
};

/**
 * An error answer's message in the reader's language, or null to leave it as it is. A failure - an answer carrying a
 * reference - says its code's words and the reference, never the server's English (the owner's decision 1 of 27 Oct
 * 2026, close of day): every screen that shows the message shows both.
 */
export function inReadersWords(data) {
  if (!data || typeof data !== "object") {
    return null;
  }
  if (data.reference) {
    const known = Object.prototype.hasOwnProperty.call(FAILURE_WORDS, data.code);
    const words = known ? FAILURE_WORDS[data.code]() : i18n.t("failureUnexpected");
    return `${words} ${i18n.t("failureReference", { reference: data.reference })}`;
  }
  return Object.prototype.hasOwnProperty.call(REFUSAL_WORDS, data.code) ? REFUSAL_WORDS[data.code]() : null;
}

const initializeApp = () => {
  // Checklist 1.6, row 10 (owner, 17 Oct 2026): the member list once kept the member being edited - number included - in
  // localStorage as "editingMember", and only a logout from the header cleared it. Nothing writes it now; a copy left on
  // a shared office computer goes at the next start.
  localStorage.removeItem("editingMember");

  // Create a request interceptor
  axios.interceptors.request.use(
    (config) => {
      // Check if the URL is not the login endpoint
      if (config.url !== AUTHENTICATE_URL) {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // A 401 means the backend found no valid Authentication at all - missing, malformed, or
  // expired JWT (JwtAuthenticationEntryPoint, cmms repo) - as opposed to a 403, which means an
  // authenticated caller lacks permission and should keep going through each screen's existing
  // handling (e.g. AccessDenied). Excluded here: the login request itself, whose own 401 means
  // "wrong username/password" (Login.js already shows that inline) - without this exclusion a
  // bad password would redirect back to /login instead of showing an error, a redirect loop in
  // effect. See docs/decisions.md.
  if (!responseInterceptorRegistered) {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // A date sent with a time of day (the owner's decision 1 of 20 Oct 2026, SC3): the server says, in English and
        // Swedish, to reload the page - for a page loaded before the screens sent days, which shows its words as they
        // are. A page that knows the code says it in its reader's language, on every screen that shows the message.
        if (error.response?.data?.code === "DATE_SENT_WITH_TIME") {
          error.response.data = { ...error.response.data, message: i18n.t("pageOutOfDate") };
        }
        const said = inReadersWords(error.response?.data);
        if (said) {
          error.response.data = { ...error.response.data, message: said };
        }
        const isLoginRequest = error.config?.url === AUTHENTICATE_URL;
        if (error.response?.status === 401 && !isLoginRequest) {
          localStorage.removeItem("token");
          localStorage.removeItem("currentUser");
          if (window.location.pathname !== "/login") {
            // Two-step sign-in (the account package plan, 6.4): a session won with the password alone, for an account
            // that has a second step or must have one now, is told so - not as an expired session.
            window.location.href = error.response?.data?.code === "SECOND_STEP_REQUIRED"
              ? "/login?secondStepNeeded=1" : "/login?sessionExpired=1";
          }
        }
        return Promise.reject(error);
      }
    );
    responseInterceptorRegistered = true;
  }

  if (!unhandledRejectionsSaid) {
    window.addEventListener("unhandledrejection", sayAnUnhandledRejection);
    unhandledRejectionsSaid = true;
  }

  // Setting base URL for all API request via axios
  axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;

  // "Who am I" is no longer asked here: it was asked without anyone waiting for the answer, and a failure dropped the
  // stored user without a word. The signed-in layout asks it now, waits for it, and says a failure
  // (features/identity/IdentityContext.js - tier 1, item 1).

  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    // dev code
  } else {
    // Prod build code

    // Removing console.log from prod
    console.log = () => {};

    // init analytics here
  }
};

export default initializeApp;
